import React, { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "./dialog";
import { Button } from "./button";
import { Input } from "./input";

interface ConfirmOptions {
	/** Label of the confirming button; defaults to "Confirm". */
	confirmLabel?: string;
	/** Red confirming button (deletes, cancels). Defaults to true. */
	destructive?: boolean;
}

type Request =
	| { kind: "confirm"; message: string; options: ConfirmOptions; resolve: (ok: boolean) => void }
	| { kind: "prompt"; message: string; options: ConfirmOptions; resolve: (value: string | null) => void };

let enqueue: ((request: Request) => void) | null = null;

/**
 * In-app replacement for `window.confirm`: translated, right-to-left aware, styled like the rest of the
 * dashboard. Resolves to true when the user confirms. Requires `<ConfirmDialogHost />` to be mounted.
 */
export function confirmAction(message: string, options: ConfirmOptions = {}): Promise<boolean> {
	return new Promise(resolve => {
		if (!enqueue) return resolve(window.confirm(message));
		enqueue({ kind: "confirm", message, options, resolve });
	});
}

/** In-app replacement for `window.prompt`: resolves to the entered text, or null when cancelled. */
export function promptAction(message: string, options: ConfirmOptions = {}): Promise<string | null> {
	return new Promise(resolve => {
		if (!enqueue) return resolve(window.prompt(message));
		enqueue({ kind: "prompt", message, options, resolve });
	});
}

/** Mounted once at the app root; renders whichever confirmation is currently pending. */
export function ConfirmDialogHost() {
	const { t } = useTranslation();
	const [request, setRequest] = useState<Request | null>(null);
	const [value, setValue] = useState("");
	const confirmRef = useRef<HTMLButtonElement>(null);

	useEffect(() => {
		enqueue = next => {
			setValue("");
			setRequest(next);
		};
		return () => {
			enqueue = null;
		};
	}, []);

	const close = (confirmed: boolean) => {
		if (!request) return;
		if (request.kind === "confirm") request.resolve(confirmed);
		else request.resolve(confirmed ? value : null);
		setRequest(null);
	};

	const destructive = request?.options.destructive ?? true;

	return (
		<Dialog open={request != null} onOpenChange={open => !open && close(false)}>
			<DialogContent
				className="max-w-md"
				onOpenAutoFocus={event => {
					if (request?.kind === "confirm") {
						event.preventDefault();
						confirmRef.current?.focus();
					}
				}}
			>
				<form
					className="space-y-4"
					onSubmit={event => {
						event.preventDefault();
						close(true);
					}}
				>
					<DialogHeader className="pe-8">
						<DialogTitle>{t("common.confirm")}</DialogTitle>
						<DialogDescription className="text-base text-foreground">{request?.message}</DialogDescription>
					</DialogHeader>
					{request?.kind === "prompt" && (
						<Input value={value} onChange={e => setValue(e.target.value)} aria-label={request.message} />
					)}
					<DialogFooter className="gap-2">
						<Button type="button" variant="outline" onClick={() => close(false)}>
							{t("common.cancel")}
						</Button>
						<Button ref={confirmRef} type="submit" variant={destructive ? "destructive" : "default"}>
							{request?.options.confirmLabel ?? t("common.confirm")}
						</Button>
					</DialogFooter>
				</form>
			</DialogContent>
		</Dialog>
	);
}
